import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import { ReactComponent as DownIcon } from "../../assets/images/down_arrow.svg";
import { getRestaurantData, addFeatureToRestaurant, removeFeatureFromRestaurant } from '../../api';
import { updateRestaurantData, updateFeaturesList } from "../../redux/actions/userDataActions";

import Container from "../../components/Container";
import AlertMsg from "../../components/AlertMsg";
import Loader from "../../components/Loader";
import RestaurantHeader from "../../components/RestaurantHeader";
import { featureServices } from "../../api/useQueryHooks";

import FeatureCard from "./FeatureCard";

const getSelectedFeaturesObject = (featuresList) => {
    const ids = {};
    for (const featureId of featuresList) ids[featureId] = true;
    return ids;
}

const FeaturesList = (props) => {
    const navigate = useNavigate();
    const { rest_id } = useParams();
    const [selectedFeatureIds, setSelectedFeatureIds] = useState(getSelectedFeaturesObject(props.featuresList));
    const [togglingFeature, setTogglingFeature] = useState(false);
    const [togglingFeatureId, setTogglingFeatureId] = useState(null);

    const [msgAlert, setMsgAlert] = useState({
        open: false,
        message: "",
        msgType: "error",
    });

    const { data: featureGroupList, isLoading: isLoadingFeatureGroupList } = featureServices.useGetAllGroups();

    useEffect(() => {
        getRestaurantData(rest_id).then((response) => {
            if (response.success) props.updateRestaurantData(response.data);
        });
    }, []);

    useEffect(() => {
        setSelectedFeatureIds(getSelectedFeaturesObject(props.featuresList));
    }, [props.featuresList]);

    const handleFeatureToggle = async (feature, checked) => {
        setTogglingFeature(true);
        setTogglingFeatureId(feature._id);
        const response = checked ? await addFeatureToRestaurant(rest_id, [feature._id]) : await removeFeatureFromRestaurant(rest_id, [feature._id]);
        setTogglingFeature(false);
        setTogglingFeatureId(null);
        if (response.success) {
            setSelectedFeatureIds(checked ? { ...selectedFeatureIds, [feature._id]: true } : { ...selectedFeatureIds, [feature._id]: false });
            props.updateFeaturesList(checked ? [...props.featuresList, feature._id] : props.featuresList.filter(id => id !== feature._id));
            setMsgAlert({
                open: true,
                message: `Feature ${checked ? "added" : "removed"} successfully`,
                msgType: "success"
            })
        } else {
            setMsgAlert({
                open: true,
                message: response.msg,
                msgType: "error"
            })
        }
    }

    const getActiveFeaturesCount = (features) => {
        let count = 0;
        for (const feature of features) {
            if (selectedFeatureIds[feature._id]) count++;
        }
        return count;
    }

    return (
        <Container
            page={"restaurants"}
            searchVal={""}
            onSearch={() => null}
            searchLabel=""
        >
            {isLoadingFeatureGroupList ? (
                <Loader height={{ height: window.innerHeight - 150 }} />
            ) : (
                <Grid container item xs={12}>
                    <Grid item xs={12} mb={2}>
                        <RestaurantHeader
                            title={"Features List"}
                            onBackBtnClick={() => navigate("/restaurant/" + rest_id)}
                        />
                    </Grid>
                    <Grid xs={12} sx={styles.mainView}>
                        {featureGroupList.map((group, index) =>
                            <Accordion key={group.id}>
                                <AccordionSummary
                                    expandIcon={<DownIcon width={"2rem"} height={"2rem"} />}
                                    aria-controls="panel-content"
                                    id="panel-header"
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{group.groupName}</Typography>
                                        <Box sx={styles.activeContainer}>
                                            <Box sx={{ mr: '0.4rem', fontWeight: 600 }}>Active/Total: </Box>{getActiveFeaturesCount(group.features)}/{group.features.length}
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container>
                                        {group.features.map((feature) => {
                                            return (<Grid item xs={12} md={6} lg={4} key={feature._id}>
                                                <FeatureCard
                                                    key={feature._id}
                                                    featureData={feature}
                                                    isChecked={selectedFeatureIds[feature._id]}
                                                    onToggle={handleFeatureToggle}
                                                    toggling={togglingFeature && togglingFeatureId === feature._id}
                                                />

                                            </Grid>)
                                        })}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>)
                        }
                    </Grid>
                </Grid>
            )
            }
            <AlertMsg
                msgAlert={msgAlert}
                onCloseAlertMsg={() =>
                    setMsgAlert({ open: false, message: "", msgType: "error" })
                }
            />
        </Container >
    )
}

const mapStateToProps = (state) => ({
    featuresList: state.userData.restaurantData?.featureFlags ?? [],
});

const mapDispatchToProps = (dispatch) => ({
    updateFeaturesList: (featuresList) => dispatch(updateFeaturesList(featuresList)),
    updateRestaurantData: (data) => dispatch(updateRestaurantData(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FeaturesList);

const styles = {
    mainView: {
        px: 3,
        py: 2,
        minHeight: 'calc(100vh - 10rem)',
    },
    groupTitle: {
        backgroundColor: "#ffffff",
        color: "#000000",
        fontSize: "14px",
        fontFamily: "GilroySemiBold",
    },
    featureContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: '1rem',
    },
    activeContainer: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        padding: '6px 12px',
        backgroundColor: "#f9f9f9",
        borderRadius: '0.5rem',
        fontSize: '0.75rem',
        marginRight: '0.5rem'
    }
}
